/**
 * CS health history — the daily snapshot collection (`healthHistory`)
 * behind the customer-health trend.
 *
 * WHY this exists: customer health is scored on demand (src/lib/crm/health.ts)
 * so a single number can't tell you whether an account is climbing or
 * sliding. Persisting one honest point per customer per day turns the
 * health score into a trajectory — "we moved health 4→8".
 *
 * WHY one row per customer per day (id === `${personId}:${date}`): the
 * score recomputes on every data change, so a naive log would be noise.
 * Keying the record by the customer + local calendar date and letting the
 * latest reading of the day win collapses that into exactly one
 * trustworthy point per customer per day.
 *
 * Honest accounting: like everything else this is deterministic and
 * computed from live store data; in mock mode it lives in localStorage
 * and flips to Firestore with the same flag as the rest of the app.
 * Mirrors src/lib/db/aqiHistory.ts precisely.
 */

import { db, nowIso } from './store';
import type { CustomerHealthSnapshot } from './types';
import type { CustomerHealth } from '../crm/health';

/** One customer's current health reading, ready to snapshot. */
export interface HealthSnapshotSource {
  personId: string;
  health: Pick<CustomerHealth, 'score' | 'band'>;
}

/** Local calendar date key (YYYY-MM-DD) — the per-day snapshot key. */
export function healthDateKey(now: number = Date.now()): string {
  const d = new Date(now);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Persist today's customer-health readings as daily snapshots — one row
 * per customer per local date. Idempotent per (customer, day): the latest
 * reading of the day overwrites earlier ones, and an unchanged score is a
 * no-op so we never churn subscriptions. Failures are swallowed — the
 * trend is a nice-to-have and must never break the CS render that feeds it.
 */
export async function recordHealthSnapshots(
  healths: HealthSnapshotSource[],
  now: number = Date.now(),
): Promise<void> {
  try {
    const date = healthDateKey(now);
    const all = await db.list('healthHistory');
    for (const { personId, health } of healths) {
      const existing = all.find((s) => s.personId === personId && s.date === date);
      if (existing) {
        if (existing.score === health.score && existing.band === health.band) continue; // no movement → no write
        await db.update('healthHistory', existing.id, {
          score: health.score,
          band: health.band,
          updatedAt: nowIso(),
        });
      } else {
        await db.add('healthHistory', {
          id: `${personId}:${date}`,
          personId,
          date,
          score: health.score,
          band: health.band,
          updatedAt: nowIso(),
        });
      }
    }
  } catch {
    /* trend is a nice-to-have; never break the CS view */
  }
}

/** All snapshots (optionally for one customer), oldest first. */
export async function listHealthHistory(personId?: string): Promise<CustomerHealthSnapshot[]> {
  const all = await db.list('healthHistory');
  return all
    .filter((s) => !personId || s.personId === personId)
    .sort((a, b) => a.date.localeCompare(b.date));
}

/** Subscribe to the snapshot history (oldest first). */
export function subscribeHealthHistory(
  fn: (rows: CustomerHealthSnapshot[]) => void,
): () => void {
  return db.subscribe('healthHistory', (rows) =>
    fn([...rows].sort((a, b) => a.date.localeCompare(b.date))),
  );
}
